import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Rating,
  TextField,
  Divider,
  Stack,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import api from "../../api/axios";
import ErrorDialog from "../../components/ErrorDialog";

/**
 * Amazon-style Review Dialog
 * Supports WRITE + EDIT
 */
const ReviewDialog = ({
  open,
  onClose,
  productId,
  orderId,
  mode = "WRITE",     // "WRITE" | "EDIT"
  reviewId = null,
  onSuccess = () => {},
}) => {
const defaultRatings = { productQuality: 0, valueForMoney: 0, deliveryExperience: 0, sellerService: 0, agentExperience: 0 };

  const [ratings, setRatings] = useState(defaultRatings);

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [video, setVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ open: false, message: "" });
  const [videoBlob, setVideoBlob] = useState(null);
  const [images, setImages] = useState([]);


  /* ============================
     LOAD EXISTING REVIEW (EDIT)
     ============================ */
  useEffect(() => {
    if (open && mode === "EDIT" && reviewId) {
      setLoading(true);

      api
        .get(`/reviews/${reviewId}`)
        .then((res) => {
          const r = res.data;
          setRatings({ ...defaultRatings, ...(r.ratings || {}) });
          setTitle(r.title || "");
          setComment(r.comment || "");
          setVideo(r.media?.videos?.[0] || null);
        })
        .catch((err) => {
          const errorMessage = err.response?.data?.message || "Failed to load review. You may not have permission to edit this review.";
          setError({ open: true, message: errorMessage });
        })
        .finally(() => setLoading(false));
      return;
    }

    if (open && mode === "WRITE") {
      setRatings(defaultRatings);
      setTitle("");
      setComment("");
      setVideo(null);
      setVideoBlob(null);
      setImages([]);
      setLoading(false);
      return;
    }

    if (!open) {
      setRatings(defaultRatings);
      setTitle("");
      setComment("");
      setVideo(null);
      setVideoBlob(null);
      setImages([]);
      setLoading(false);
    }
  }, [open, mode, reviewId]);

  const VideoRecorder = ({ onRecorded }) => {
  const [recording, setRecording] = useState(false);
  const mediaRef = React.useRef(null);
  const chunks = React.useRef([]);

  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });

    const recorder = new MediaRecorder(stream);
    mediaRef.current = { recorder, stream };

    recorder.ondataavailable = (e) => chunks.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks.current, { type: "video/webm" });
      chunks.current = [];
      onRecorded(blob);
      stream.getTracks().forEach((t) => t.stop());
    };

    recorder.start();
    setRecording(true);
  };

  const stop = () => {
    mediaRef.current.recorder.stop();
    setRecording(false);
  };

  return (
    <Button
      variant="outlined"
      startIcon={<VideocamIcon />}
      onClick={recording ? stop : start}
    >
      {recording ? "Stop recording" : "Record video"}
    </Button>
  );
};

const ImageUploader = ({ onChange }) => (
  <Button component="label" variant="outlined">
    Upload images
    <input
      hidden
      type="file"
      accept="image/*"
      multiple
      onChange={(e) =>
        onChange(Array.from(e.target.files).slice(0, 5))
      }
    />
  </Button>
);


  const [validationErrors, setValidationErrors] = useState({});

  const handleRatingChange = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, rating: "" }));
  };

  const hasAnyRating = Object.values(ratings).some(v => v > 0);

  const canSubmit =
    hasAnyRating &&
    comment.trim().length >= 10 &&
    !submitting &&
    !loading;

  /* ============================
     SUBMIT (WRITE / EDIT)
     ============================ */
const handleSubmit = async () => {
  const errors = {};
  if (!Object.values(ratings).some(v => v > 0)) errors.rating = "Please select at least 1 star rating";
  if (comment.trim().length < 10) errors.comment = "Please write at least 10 characters";
  if (Object.keys(errors).length) {
    setValidationErrors(errors);
    return;
  }

  try {
    setSubmitting(true);

    if (videoBlob && videoBlob.size > 20 * 1024 * 1024) {
          alert("Video must be under 20MB");
          return;
        }

        for (const img of images) {
          if (img.size > 5 * 1024 * 1024) {
            alert("Each image must be under 5MB");
            return;
          }
        }

    // 1️⃣ Create / update review (TEXT ONLY)
    let reviewRes;

    const payload = {
      productId,
      orderId,
      ratings,
      title,
      comment,
    };

    if (mode === "EDIT") {
      console.log("Updating review, URL:", `/reviews/${reviewId}`);
      reviewRes = await api.patch(`/reviews/${reviewId}`, payload);
    } else {
      reviewRes = await api.post("/reviews", payload);
    }

    const id =
      mode === "EDIT"
        ? reviewId
        : reviewRes.data._id;

    // 2️⃣ Upload media AFTER review exists
    if (videoBlob || images.length) {
      const formData = new FormData();
      if (videoBlob) formData.append("video", videoBlob);
      images.forEach((img) => formData.append("images", img));

      console.log("Uploading media, URL:", `/reviews/${id}/media`);
      await api.post(`/reviews/${id}/media`, formData);
    }

    onSuccess();
    onClose();
  } catch (err) {
    console.error("Failed to submit review", err);
    const errorMessage = err.response?.data?.message || "An unexpected error occurred. Please try again.";
    setError({ open: true, message: errorMessage });
  } finally {
    setSubmitting(false);
  }
};


  return (
    <Box>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {mode === "EDIT"
          ? "Edit your review"
          : "Write a customer review"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography>Loading review…</Typography>
        ) : (
          <>
            {/* Ratings */}
            {validationErrors.rating && (
              <Typography variant="caption" color="error" sx={{ mb: 0.5 }}>
                {validationErrors.rating}
              </Typography>
            )}
            <Stack spacing={2}>
              <RatingRow
                label="Product quality"
                value={ratings.productQuality}
                onChange={(v) =>
                  handleRatingChange("productQuality", v)
                }
              />
              <RatingRow
                label="Value for money"
                value={ratings.valueForMoney}
                onChange={(v) =>
                  handleRatingChange("valueForMoney", v)
                }
              />
              <RatingRow
                label="Delivery experience"
                value={ratings.deliveryExperience}
                onChange={(v) =>
                  handleRatingChange("deliveryExperience", v)
                }
              />
              <RatingRow
                label="Vendor service"
                value={ratings.sellerService}
                onChange={(v) =>
                  handleRatingChange("sellerService", v)
                }
              />
              {/* <RatingRow
                label="Vendor experience"
                value={ratings.vendorExperience}
                onChange={(v) =>
                  handleRatingChange("vendorExperience", v)
                }
              /> */}
              <RatingRow
                label="Agent service"
                value={ratings.agentExperience}
                onChange={(v) =>
                  handleRatingChange("agentExperience", v)
                }
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Title */}
            <Typography fontWeight={600} mb={0.5}>
              Add a headline
            </Typography>
            <TextField
              fullWidth
              placeholder="What's most important to know?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
            />

            {/* Comment */}
            <Typography fontWeight={600} mt={2} mb={0.5}>
              Write your review
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="What did you like or dislike?"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (e.target.value.trim().length >= 10) {
                  setValidationErrors((prev) => ({ ...prev, comment: "" }));
                }
              }}
              error={!!validationErrors.comment}
              helperText={validationErrors.comment || `${comment.trim().length}/10 characters minimum`}
            />

            {/* Video */}
            <Box sx={{ mt: 2 }}>
              <Typography fontWeight={600} mb={0.5}>
                Add a video review (optional)
              </Typography>

              <Stack spacing={1}>
                <VideoRecorder onRecorded={setVideoBlob} />
                <ImageUploader onChange={setImages} />

                {(videoBlob || images.length > 0) && (
                  <Typography variant="caption">
                    {videoBlob && "🎥 Video added "}
                    {images.length > 0 && `🖼 ${images.length} images added`}
                  </Typography>
                )}
              </Stack>


              {video && (
                <Typography variant="caption" display="block" mt={1}>
                  🎥 Video added
                </Typography>
              )}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              Only verified buyers can submit reviews.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submitting || loading}
          onClick={handleSubmit}
        >
          {mode === "EDIT" ? "Update review" : "Submit review"}
        </Button>
      </DialogActions>
    </Dialog>
    <ErrorDialog
      open={error.open}
      onClose={() => setError({ open: false, message: "" })}
      message={error.message}
    />
  </Box>
);
}

/* =====================
   Rating Row
   ===================== */
const RatingRow = ({ label, value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <Typography sx={{ fontSize: 14 }}>{label}</Typography>
    <Rating value={value} onChange={(_, v) => onChange(v)} />
  </Box>
);

export default ReviewDialog;